import { Injectable } from '@angular/core';
import { ModalService } from 'app/core/modal';
import { TrackingPageFeedbackModalComponent } from './modals';
import { isTruthy } from 'app/helpers';
import { FontPickerService } from 'ngx-font-picker';
import {
  TrackingPageModel,
  TrackingPageItemWithFont
} from './tracking-page.model';
import { environment } from '../../../environments/environment';
import { last } from 'ramda';
import { get } from 'lodash';
import { MaterialModalService } from 'app/core/modal/material-modal.service';

@Injectable()
export class TrackingPageCoreService {
  private faviconAdded: boolean;
  private headerScript: HTMLDivElement;
  private footerScript: HTMLDivElement;
  private inputStyleSheet: HTMLStyleElement;
  private scrollStyleSheet: HTMLStyleElement;
  private fontsStyleSheets: { [key in TrackingPageItemWithFont]: any } = {
    card: null,
    sidebar: null,
    footer: null,
    searchButton: null,
    searchPageInput: null,
    trackButton: null,
    searchPageTitle: null,
    detailsButton: null,
    'mainProductsFeed.card.button': null,
    'mainProductsFeed.card.title': null,
    'mainProductsFeed.card.regularPrice': null,
    'mainProductsFeed.card.discountedPrice': null,
    'sidebarProductsFeed.card.button': null,
    'sidebarProductsFeed.card.title': null,
    'sidebarProductsFeed.card.regularPrice': null,
    'sidebarProductsFeed.card.discountedPrice': null
  };

  constructor(
    private modalService: ModalService,
    public matModalService: MaterialModalService,
    private fontPickerService: FontPickerService
  ) {}

  processTrackingPage(trackingPage: TrackingPageModel, visualBuilder: boolean) {
    this.loadFonts(trackingPage);
    this.addInputStyles(trackingPage);
    this.addScrollStyles(trackingPage);

    if (!visualBuilder) {
      this.addFavicon(trackingPage);
      this.addScripts(trackingPage);
      this.addSeoTitle(trackingPage);
    }
  }

  addScrollStyles(trackingPage: TrackingPageModel) {
    const { pageScroll } = trackingPage;

    if (!this.scrollStyleSheet) {
      this.scrollStyleSheet = this.addStyleSheetElement();
    }

    const styles = `
      .tracking-page-items::scrollbar-thumb {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.scrollColor};
      }
      .tracking-page-items::scrollbar-track {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.backgroundColor};
      }

      app-tracking-page app-tracking-page-sidebar .wrapper-status .all-status::scrollbar-track {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.backgroundColor};
      }
      app-tracking-page app-tracking-page-sidebar .wrapper-status .all-status::scrollbar-thumb {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.scrollColor};
      }

      .tracking-page-items::-webkit-scrollbar-thumb {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.scrollColor};
      }
      .tracking-page-items::-webkit-scrollbar-track {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.backgroundColor};
      }

      app-tracking-page app-tracking-page-sidebar .wrapper-status .all-status::-webkit-scrollbar-track {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.backgroundColor};
      }
      app-tracking-page app-tracking-page-sidebar .wrapper-status .all-status::-webkit-scrollbar-thumb {
        border-radius: ${pageScroll.borderRadius}px;
        background: ${pageScroll.scrollColor};
      }
    `;
    this.scrollStyleSheet.innerHTML = styles;
  }

  addScripts(trackingPage: TrackingPageModel) {
    const { head, body } = trackingPage.scripts;

    if (head) {
      this.headerScript = this.generateScript(head, 'head');
    }

    if (body) {
      this.footerScript = this.generateScript(body, 'body');
    }
  }

  addInputStyles(trackingPage: TrackingPageModel) {
    if (!this.inputStyleSheet) {
      this.inputStyleSheet = this.addStyleSheetElement();
    }

    const styles = `
      app-tracking-page-search .checking-section input.form-control::placeholder {
        font-family: ${
          trackingPage.searchPageInput.font.customFontUsed
            ? `'${trackingPage.searchPageInput.font.customFontName}'`
            : trackingPage.searchPageInput.font.fontFamily
        };
      }
    `;
    this.inputStyleSheet.innerHTML = styles;
  }

  addFavicon(trackingPage: TrackingPageModel) {
    if (this.faviconAdded) {
      return;
    }

    const url = trackingPage.faviconUrl
      ? trackingPage.faviconUrl
      : '/assets/favicon/favicon.ico';
    const favicon = document.querySelector('link[rel*=\'icon\']');
    const extenstion = last(url.split('.'));
    const type = extenstion === 'icon' ? 'image/x-icon' : `image/${extenstion}`;
    favicon.setAttribute('rel', 'icon');
    favicon.setAttribute('type', type);
    favicon.setAttribute('href', url);

    if (url !== 'favicon.ico') {
      this.faviconAdded = true;
    }
  }

  getDomain() {
    const isDomainOnly = window.location.hostname.split('.').length === 2;
    if (isDomainOnly) {
      return window.location.hostname;
    }
    const [subdomain, ...domain] = window.location.hostname.split('.');
    return this.isCustomDomain(domain.join('.'))
      ? window.location.hostname
      : subdomain;
  }

  loadFonts(trackingPage: TrackingPageModel) {
    (
      [
        'languageMenu',
        'sidebar',
        'card',
        'footer',
        'searchPageInput',
        'searchPageTitle',
        'searchButton',
        'detailsButton',
        'trackButton',
        'mainProductsFeed.card.button',
        'mainProductsFeed.card.title',
        'mainProductsFeed.card.regularPrice',
        'mainProductsFeed.card.discountedPrice',
        'sidebarProductsFeed.card.button',
        'sidebarProductsFeed.card.title',
        'sidebarProductsFeed.card.regularPrice',
        'sidebarProductsFeed.card.discountedPrice',
        'orderCard',
        'orderStatus',
        'orderNumber',
        'orderLastUpdated',
        'orderItemsFrame.button',
        'orderItemsFrame.itemCard.title',
        'orderItemsFrame.itemCard.info',
        'notYetShippedItemsTitle'
      ] as TrackingPageItemWithFont[]
    ).forEach(this.loadFont(trackingPage));
  }

  showFeedbackModal({ data, number }, callback) {
    this.matModalService
      .openModal(
        TrackingPageFeedbackModalComponent,
        { data, number },
        { maxWidth: '688px' }
      )
      .result$.subscribe((result) => {
        if (!result) {
          return;
        }
        this.matModalService.closeModal();
        callback(result);
      });
  }

  getTrackingPageBackground(trackingPage: TrackingPageModel) {
    return trackingPage?.background?.useBackgroundImage
      ? {
          'background-image': `url(${trackingPage.background.imageUrl})`,
          'background-size':
            trackingPage.background.backgroundMode === 'pattern'
              ? 'auto'
              : 'cover',
          'background-repeat': trackingPage.background.backgroundRepeat,
          'background-color': 'unset',
          'background-position': trackingPage.background.backgroundPosition
        }
      : {
          'background-image': 'none',
          'background-color': trackingPage.background.color || '#fff'
        };
  }

  getCustomFonts(trackingPage: TrackingPageModel) {
    return [
      trackingPage.sidebar.font.customFontName,
      trackingPage.card.font.customFontName,
      trackingPage.searchButton.font.customFontName,
      trackingPage.footer.font.customFontName,
      trackingPage.searchPageTitle.font.customFontName,
      trackingPage.searchButton.font.customFontName,
      trackingPage.detailsButton.font.customFontName,
      trackingPage.trackButton.font.customFontName
    ]
      .filter(isTruthy)
      .reduce((acc, curr) => (acc.includes(curr) ? acc : [...acc, curr]), []);
  }

  clearElements() {
    const fontElements = Object.values(this.fontsStyleSheets).filter(isTruthy);
    Object.keys(this.fontsStyleSheets).forEach(
      (key) => (this.fontsStyleSheets[key] = null)
    );
    [
      this.headerScript,
      this.footerScript,
      this.inputStyleSheet,
      this.scrollStyleSheet,
      ...fontElements
    ].forEach(this.clearElement);
  }

  private clearElement(element: HTMLDivElement | HTMLStyleElement) {
    if (!element) {
      return;
    }
    element.remove();
  }

  private isCustomDomain(domain: string): boolean {
    return !environment.appUrl.includes(domain);
  }

  private loadFont(trackingPage: TrackingPageModel) {
    return (key: TrackingPageItemWithFont) => {
      const {
        fontFamily,
        customFontName,
        customFontUrl,
        customFontUsed,
        fontStyle
      } = get(trackingPage, key).font;

      if (!customFontUsed) {
        this.fontsStyleSheets[key] = null;
        return this.fontPickerService.loadFont({
          family: fontFamily,
          style: fontStyle,
          size: '16px'
        });
      }

      if (!customFontUrl) {
        return;
      }

      this.fontsStyleSheets[key] = this.fontsStyleSheets[key]
        ? this.fontsStyleSheets[key]
        : this.addStyleSheetElement();

      const extension = customFontUrl.split('.').pop();
      const format = extension === 'ttf' ? 'truetype' : extension;

      const style = `
        @font-face {
          font-family: "${customFontName}";
          src:url("${customFontUrl}") format("${format}");
        }
      `;

      this.fontsStyleSheets[key].innerHTML = style;
    };
  }

  private addStyleSheetElement(): HTMLStyleElement {
    const element = document.createElement('style');
    element.appendChild(document.createTextNode(''));
    document.head.appendChild(element);
    return element;
  }

  private generateScript(code, position): HTMLDivElement {
    const scriptEl = document.createElement('div');
    scriptEl.innerHTML = code;

    const scripts = Array.prototype.slice.apply(
      scriptEl.getElementsByTagName('script')
    );
    const stylesheets = Array.prototype.slice.apply(
      scriptEl.getElementsByTagName('style')
    );

    scripts.forEach((script) => this.addJsScript(script, position));
    stylesheets.forEach((stylesheet) =>
      this.addStyleSheet(stylesheet, position)
    );
    return scriptEl;
  }

  private addStyleSheet(stylesheet: HTMLStyleElement, position: string) {
    const content = stylesheet.innerText;
    const attributes = stylesheet.getAttributeNames();

    const newStyles = document.createElement('style');
    newStyles.appendChild(document.createTextNode(content));

    attributes.forEach((key) => {
      newStyles.setAttribute(key, stylesheet.getAttribute(key));
    });

    if (position === 'head') {
      document.head.appendChild(newStyles);
    }
    document.body.appendChild(newStyles);
  }

  private addJsScript(script: HTMLScriptElement, position: string) {
    const content = script.innerText;
    const attributes = script.getAttributeNames();

    const newScript = document.createElement('script');
    newScript.type = 'text/javascript';
    attributes.forEach((key) => {
      newScript.setAttribute(key, script.getAttribute(key));
    });
    try {
      newScript.appendChild(document.createTextNode(content));
    } catch (e) {
      newScript.text = content;
    }
    if (position === 'head') {
      document.head.appendChild(newScript);
    } else {
      document.body.appendChild(newScript);
    }
  }

  private addSeoTitle(trackingPage: TrackingPageModel) {
    if (trackingPage.seoTitle) {
      const title = document.querySelector('title');
      title.innerText = trackingPage.seoTitle;
    }
  }
}
